import React from "react";
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  StopIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import type { ProcessingMode } from "../lib/cleanseSchema";
import Tooltip from "./Tooltip";

export interface SidebarProps {
  mode: ProcessingMode;
  setMode: (m: ProcessingMode) => void;

  rows: Record<string, any>[];
  csvHeaders: string[];
  uploadedFileName: string;
  isDragOver: boolean;

  handleFile: (file?: File | null) => void | Promise<void>;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;

  error: string | null;

  hasRecoveryData: boolean;
  resumeFromCrash: () => void;
  clearRecovery: () => void;

  line1Col: string;
  postcodeCol: string;
  uprnCol: string;
  setLine1Col: (v: string) => void;
  setPostcodeCol: (v: string) => void;
  setUprnCol: (v: string) => void;

  apiKey: string;
  setApiKey: (v: string) => void;
  apiKeyValid: boolean | null;
  validatingApiKey: boolean;

  processing: boolean;
  canProcess: boolean;
  processAll: () => void | Promise<void>;
  cancelProcessing: () => void;

  handleKeyDown: (e: React.KeyboardEvent, action: () => void) => void;
}

const sectionLabel = "text-xs/6 font-semibold text-gray-400";

export function Sidebar(props: SidebarProps) {
  const {
    mode,
    setMode,
    rows,
    csvHeaders,
    uploadedFileName,
    isDragOver,
    handleFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    error,
    hasRecoveryData,
    resumeFromCrash,
    clearRecovery,
    line1Col,
    postcodeCol,
    uprnCol,
    setLine1Col,
    setPostcodeCol,
    setUprnCol,
    apiKey,
    setApiKey,
    apiKeyValid,
    validatingApiKey,
    processing,
    canProcess,
    processAll,
    cancelProcessing,
    handleKeyDown,
  } = props;

  const insufficientCredits = error?.includes("Insufficient API credits");

  return (
    <aside className="w-80 max-w-80 shrink-0 bg-gray-900 border-r border-white/10 flex flex-col overflow-y-auto">
      <div className="px-6 pt-6 pb-4 flex justify-center">
        <a
          href="https://ideal-postcodes.co.uk"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/ideal-postcodes-logo-white.png"
            alt="Ideal Postcodes Logo"
            className="h-10 w-auto"
          />
        </a>
      </div>

      <div className="flex-1 px-6 pb-6 space-y-6">
        {rows.length === 0 && (
          <section>
            <div className={`${sectionLabel} mb-2`}>Processing Mode</div>
            <div className="flex gap-1 p-1 bg-white/5 rounded-lg border border-white/10">
              <button
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-theme-primary-500 ${
                  mode === "cleanse"
                    ? "bg-theme-primary-600 text-white shadow"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
                onClick={() => setMode("cleanse")}
              >
                Address Cleanse
              </button>
              <button
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-theme-primary-500 ${
                  mode === "uprn"
                    ? "bg-theme-primary-600 text-white shadow"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
                onClick={() => setMode("uprn")}
              >
                UPRN Lookup
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {mode === "cleanse"
                ? "Cleanse and standardise addresses using address + postcode"
                : "Lookup full address details using UPRN"}
            </p>
          </section>
        )}

        <section>
          <div className={`${sectionLabel} mb-2`}>CSV Upload</div>
          <p className="text-sm text-gray-400 mb-4">
            Upload a CSV of customer addresses. You can process up to 1,100
            addresses at a time.
          </p>

          <label className="label">Upload CSV</label>

          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            tabIndex={0}
            role="button"
            aria-label="Upload CSV file by clicking or dragging and dropping"
            onKeyDown={(e) =>
              handleKeyDown(e, () => {
                const input = e.currentTarget.querySelector(
                  'input[type="file"]'
                ) as HTMLInputElement;
                input?.click();
              })
            }
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 mb-4 focus:outline-hidden focus:ring-2 focus:ring-theme-primary-500 focus:border-theme-primary-500 ${
              isDragOver
                ? "border-theme-primary-400 bg-theme-primary-900/30 text-theme-primary-200"
                : uploadedFileName
                  ? "border-success-500 bg-success-900/20"
                  : "border-white/10 hover:border-white/20 text-gray-400"
            }`}
          >
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              tabIndex={-1}
            />

            {uploadedFileName ? (
              <div className="flex flex-col items-center gap-3">
                <CheckCircleIcon className="size-8 text-success-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-success-300 mb-1">
                    {uploadedFileName}
                  </p>
                  {rows.length > 0 && (
                    <p className="text-xs text-success-400 mb-2">
                      {rows.length} rows loaded successfully
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    Click or drag to change file
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <ArrowUpTrayIcon
                  className={`size-8 ${
                    isDragOver
                      ? "text-theme-primary-400"
                      : "text-gray-500"
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-medium ${
                      isDragOver
                        ? "text-theme-primary-200"
                        : "text-gray-300"
                    }`}
                  >
                    {isDragOver
                      ? "Drop your CSV file here"
                      : "Drop CSV file here or click to browse"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supports .csv files only
                  </p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div
              className={`mt-4 p-3 rounded-lg ${
                insufficientCredits
                  ? "bg-comp-900/20 border border-comp-500/30"
                  : "bg-red-900/20 border border-red-500/30"
              }`}
            >
              <div className="flex items-start gap-2">
                {insufficientCredits ? (
                  <InformationCircleIcon className="size-5 shrink-0 text-comp-400" />
                ) : (
                  <XCircleIcon className="size-5 shrink-0 text-red-400" />
                )}
                <div className="flex-1">
                  <span
                    className={`text-sm ${
                      insufficientCredits ? "text-comp-200" : "text-red-300"
                    }`}
                  >
                    {error}
                  </span>
                  {insufficientCredits && (
                    <div className="mt-2">
                      <a
                        href="https://account.ideal-postcodes.co.uk/tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-theme-primary-400 hover:text-theme-primary-300 underline text-sm font-medium"
                      >
                        Top up your account →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasRecoveryData && (
            <div className="mt-4 p-4 bg-comp-900/20 border border-comp-500/40 rounded-lg">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="size-5 shrink-0 text-comp-400" />
                  <div>
                    <p className="text-sm text-comp-200 font-medium mb-1">
                      Previous session found
                    </p>
                    <p className="text-xs text-comp-300">
                      Resume from where you left off or start fresh
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1.5 text-xs bg-comp-500 hover:bg-comp-400 text-white rounded-md font-medium focus:outline-hidden focus:ring-2 focus:ring-comp-400"
                    onClick={resumeFromCrash}
                    onKeyDown={(e) => handleKeyDown(e, resumeFromCrash)}
                    aria-label="Resume processing from previous session"
                  >
                    Resume Cleanse
                  </button>
                  <button
                    className="px-3 py-1.5 text-xs border border-white/10 hover:bg-white/5 text-comp-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-comp-400"
                    onClick={clearRecovery}
                    onKeyDown={(e) => handleKeyDown(e, clearRecovery)}
                    aria-label="Clear previous session and start fresh"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {csvHeaders.length > 0 && (
          <section>
            <div className={`${sectionLabel} mb-2`}>Column Mapping</div>
            <p className="text-xs text-gray-400 mb-3">
              {mode === "cleanse"
                ? "Select which columns contain your address data."
                : "Select which column contains UPRN values."}
            </p>
            <div className="space-y-3">
              {mode === "cleanse" ? (
                <>
                  <ColumnSelect
                    label="Address Column"
                    tooltip="Select the column containing the full address or first line of address."
                    value={line1Col}
                    setValue={setLine1Col}
                    csvHeaders={csvHeaders}
                  />
                  <ColumnSelect
                    label="Postcode Column"
                    tooltip="Select the column containing postcodes. This helps improve address matching accuracy."
                    value={postcodeCol}
                    setValue={setPostcodeCol}
                    csvHeaders={csvHeaders}
                  />
                </>
              ) : (
                <ColumnSelect
                  label="UPRN Column"
                  tooltip="Select the column containing UPRN (Unique Property Reference Number) values."
                  value={uprnCol}
                  setValue={setUprnCol}
                  csvHeaders={csvHeaders}
                />
              )}
            </div>
          </section>
        )}

        {csvHeaders.length > 0 && (
          <section>
            <div className={`${sectionLabel} mb-2`}>API Key</div>
            <p className="text-xs text-gray-400 mb-3">
              Add your Ideal Postcodes API key to start cleansing.{" "}
              <a
                href="https://account.ideal-postcodes.co.uk/tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-primary-400 hover:text-theme-primary-300 underline"
              >
                Get your key
              </a>
            </p>
            <div className="relative">
              <input
                type="text"
                className={`input pr-9 ${apiKeyRingClass(
                  apiKey,
                  apiKeyValid,
                  validatingApiKey
                )}`}
                placeholder="Enter your API key..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />

              {apiKey && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validatingApiKey ? (
                    <ArrowPathIcon className="size-4 text-comp-400 animate-spin" />
                  ) : apiKeyValid === true ? (
                    <CheckCircleIcon className="size-4 text-success-400" />
                  ) : apiKeyValid === false ? (
                    <XCircleIcon className="size-4 text-red-400" />
                  ) : null}
                </div>
              )}
            </div>

            {apiKey && apiKeyValid === true && (
              <p className="mt-2 text-xs text-success-400">
                API key is valid and available
              </p>
            )}
            {apiKey && apiKeyValid === false && (
              <p className="mt-2 text-xs text-red-400">
                Invalid or unavailable API key
              </p>
            )}
          </section>
        )}

        {csvHeaders.length > 0 && (
          <div className="flex items-center gap-3">
            {!processing && (
              <button
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-success-400 ${
                  canProcess
                    ? "bg-success-500 hover:bg-success-400 text-white"
                    : "border border-white/10 text-gray-500 cursor-not-allowed"
                }`}
                disabled={!canProcess}
                onClick={processAll}
                onKeyDown={(e) => handleKeyDown(e, processAll)}
                aria-label={
                  canProcess
                    ? "Start processing addresses"
                    : "Cannot start processing - missing requirements"
                }
              >
                <PlayIcon className="size-4" />
                {mode === "cleanse" ? "Start Cleanse" : "Start Lookup"}
              </button>
            )}

            {processing && (
              <button
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/40 bg-red-950/30 text-red-300 hover:bg-red-900/40 transition-colors focus:outline-hidden focus:ring-2 focus:ring-red-400"
                onClick={cancelProcessing}
                onKeyDown={(e) => handleKeyDown(e, cancelProcessing)}
                aria-label="Stop processing immediately"
              >
                <StopIcon className="size-4" />
                <span className="font-medium">Stop</span>
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function apiKeyRingClass(
  apiKey: string,
  apiKeyValid: boolean | null,
  validatingApiKey: boolean
): string {
  if (!apiKey) return "border-theme-primary-500/40";
  if (validatingApiKey) return "border-comp-500 ring-2 ring-comp-500/20";
  if (apiKeyValid === true)
    return "border-success-500 ring-2 ring-success-500/20 focus:ring-success-400";
  if (apiKeyValid === false)
    return "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400";
  return "border-theme-primary-500 ring-2 ring-theme-primary-500/20 focus:ring-theme-primary-400";
}

interface ColumnSelectProps {
  label: string;
  tooltip: string;
  value: string;
  setValue: (v: string) => void;
  csvHeaders: string[];
}

function ColumnSelect({
  label,
  tooltip,
  value,
  setValue,
  csvHeaders,
}: ColumnSelectProps) {
  const ringClass = !value
    ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400"
    : "border-success-500 ring-2 ring-success-500/20 focus:ring-success-400";

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <label className="label text-xs">{label}</label>
        <Tooltip content={tooltip}>
          <QuestionMarkCircleIcon className="size-4 text-gray-400 hover:text-gray-300 cursor-help" />
        </Tooltip>
      </div>
      <select
        aria-label={label}
        className={`input ${ringClass}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        <option value="">Select…</option>
        {csvHeaders.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </div>
  );
}
